import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined"
import MapOutlinedIcon from "@mui/icons-material/MapOutlined"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import MenuItem from "@mui/material/MenuItem"
import Snackbar from "@mui/material/Snackbar"
import TableCell from "@mui/material/TableCell"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import AdminAssignConciergeDialog from "../components/AdminAssignConciergeDialog"
import AdminConciergeUserCreateDialog from "../components/AdminConciergeUserCreateDialog"
import AdminCustomerAccessCreateDialog from "../components/AdminCustomerAccessCreateDialog"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminSmsEmployeeCreateDialog from "../components/AdminSmsEmployeeCreateDialog"
import AdminUserEditDialog from "../components/AdminUserEditDialog"
import AdminUserMlsBoardsDialog from "../components/AdminUserMlsBoardsDialog"
import {
  toCustomerAccessPayload,
  useCustomerAccessCampaigns,
} from "../components/CustomerAccessFields"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"
import BookingLinkDialog from "../meetings/BookingLinkDialog"
import { selectAdminProfile } from "../reducer"
import {
  CONCIERGE_STATIC_OPTIONS,
  JOINED_OPTIONS,
  LAST_LOGIN_OPTIONS,
  MLS_STATIC_OPTIONS,
  POD_AGENTS_TYPE_OPTION,
  SUBSCRIPTION_GROUP_COLORS,
  SUBSCRIPTION_OPTIONS,
  USER_FILTER_SPECS,
  USER_TYPE_LABELS,
  USER_TYPE_OPTIONS,
  formatRelative,
  formatShortDate,
  formatStatusText,
  hasStaffFlag,
  subscriptionDetail,
} from "./userListUtils"

export const CUSTOMER_ACCESS_FORM_PATH = "/customer-access-form/"

const mutedSx = { ...adminTableBodyCellSx, color: "text.disabled" }

function NameEmailCell({ user }) {
  return (
    <TableCell sx={{ ...adminTableBodyCellSx, maxWidth: 260 }}>
      <Column gap={0.125} sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600 }}
          noWrap
          title={user.name || ""}
        >
          {user.name || "—"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          title={user.email}
        >
          {user.email}
        </Typography>
      </Column>
    </TableCell>
  )
}

function TypeCell({ user }) {
  const pods = user.pods || []
  const label = USER_TYPE_LABELS[user.type] || "—"
  const podSuffix = pods.length
    ? ` · ${pods.length} pod${pods.length === 1 ? "" : "s"}`
    : ""
  return (
    <TableCell sx={adminTableBodyCellSx}>
      <Row gap={0.5} sx={{ flexWrap: "wrap" }}>
        <Tooltip
          title={
            pods.length ? `Pods: ${pods.map((pod) => pod.name).join(", ")}` : ""
          }
        >
          <Chip
            size="small"
            label={`${label}${podSuffix}`}
            color={user.type === "owner" ? "primary" : "default"}
            variant={user.type === "owner" ? "filled" : "outlined"}
          />
        </Tooltip>
        {user.type !== "staff" && hasStaffFlag(user) ? (
          <Chip size="small" variant="outlined" label="Staff" />
        ) : null}
      </Row>
    </TableCell>
  )
}

function SubscriptionCell({ user }) {
  const sub = user.subscription
  if (!sub) {
    return <TableCell sx={mutedSx}>None</TableCell>
  }
  const more = (user.subscription_count || 1) - 1
  return (
    <TableCell sx={adminTableBodyCellSx}>
      <Column gap={0.25} sx={{ alignItems: "flex-start" }}>
        <Row gap={0.5} sx={{ alignItems: "center" }}>
          <Chip
            size="small"
            label={formatStatusText(sub.status)}
            color={SUBSCRIPTION_GROUP_COLORS[sub.status_group] || "default"}
          />
          {more > 0 ? (
            <Typography variant="caption" color="text.secondary">
              +{more}
            </Typography>
          ) : null}
        </Row>
        <Typography variant="caption" color="text.secondary" noWrap>
          {subscriptionDetail(sub)}
        </Typography>
      </Column>
    </TableCell>
  )
}

function BoardsCell({ user }) {
  const boards = user.mls_boards || []
  const total = user.mls_board_count ?? boards.length
  if (!total) {
    return <TableCell sx={mutedSx}>None</TableCell>
  }
  const shown = boards.slice(0, 2).map((board) => board.name)
  const extra = total - shown.length
  return (
    <TableCell
      sx={{ ...adminTableBodyCellSx, maxWidth: 200 }}
      title={boards.map((board) => board.name).join(", ")}
    >
      <Typography variant="body2" noWrap>
        {shown.join(", ")}
        {extra > 0 ? ` +${extra}` : ""}
      </Typography>
    </TableCell>
  )
}

function ConciergeNamesCell({ user }) {
  const concierges = user.assigned_concierges || []
  if (!concierges.length) {
    return <TableCell sx={mutedSx}>None</TableCell>
  }
  const names = concierges.map((item) => item.name || item.email)
  return (
    <TableCell
      sx={{ ...adminTableBodyCellSx, maxWidth: 180 }}
      title={names.join(", ")}
    >
      <Typography variant="body2" noWrap>
        {names.join(", ")}
      </Typography>
    </TableCell>
  )
}

function DateCell({ value, relative = false }) {
  const text = relative ? formatRelative(value) : formatShortDate(value)
  if (!text) {
    return <TableCell sx={mutedSx}>Never</TableCell>
  }
  return (
    <TableCell
      sx={{ ...adminTableBodyCellSx, whiteSpace: "nowrap" }}
      title={new Date(value).toLocaleString()}
    >
      {text}
    </TableCell>
  )
}

const userPropType = { user: PropTypes.object.isRequired }
NameEmailCell.propTypes = userPropType
TypeCell.propTypes = userPropType
SubscriptionCell.propTypes = userPropType
BoardsCell.propTypes = userPropType
ConciergeNamesCell.propTypes = userPropType
DateCell.propTypes = { value: PropTypes.string, relative: PropTypes.bool }

export function loginKindFor(user) {
  // Texting-only employees are concierges: they log in with the concierge link.
  if (user.can_open_as_customer && !user.is_sms_only) return "customer"
  if (user.can_open_as_concierge) return "concierge"
  return null
}

function UserRowActions({
  user,
  updating,
  onIssueLink,
  onSubscription,
  onBoards,
  onAssign,
  onBookingLink,
  onEdit,
  onDeactivate,
  onReactivate,
}) {
  const [anchor, setAnchor] = useState(null)
  const loginKind = loginKindFor(user)
  const loginLabel =
    loginKind === "customer" ? "Open as customer" : "Open as concierge"

  function closeMenu(action) {
    setAnchor(null)
    action()
  }

  return (
    <Row gap={0.25} justifyContent="flex-end" sx={{ flexWrap: "nowrap" }}>
      {loginKind ? (
        <Tooltip title={loginLabel}>
          <IconButton
            size="small"
            aria-label={loginLabel}
            onClick={() => onIssueLink(user, loginKind, "open")}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title={user.subscription ? "Subscription" : "No subscription"}>
        <span>
          <IconButton
            size="small"
            aria-label="Subscription"
            disabled={!user.subscription}
            onClick={() => onSubscription(user)}
          >
            <CreditCardOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="MLS boards">
        <IconButton
          size="small"
          aria-label="MLS boards"
          onClick={() => onBoards(user)}
        >
          <MapOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {user.type === "owner" ? (
        <Tooltip title="Assign concierge">
          <IconButton
            size="small"
            aria-label="Assign concierge"
            onClick={() => onAssign(user)}
          >
            <SupportAgentOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {user.is_active && !user.is_staff && !user.is_concierge ? (
        <Tooltip title="Booking link: let someone without a login book meetings for this user">
          <IconButton
            size="small"
            aria-label="Booking link"
            onClick={() => onBookingLink(user)}
          >
            <EventAvailableOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title="Edit">
        <IconButton
          size="small"
          aria-label="Edit user"
          onClick={() => onEdit(user)}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        aria-label="More actions"
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {loginKind ? (
          <MenuItem
            onClick={() =>
              closeMenu(() => onIssueLink(user, loginKind, "copy"))
            }
          >
            Copy {loginKind} login link
          </MenuItem>
        ) : null}
        {user.can_send_mls_purchase_link ? (
          <MenuItem
            onClick={() =>
              closeMenu(() => onIssueLink(user, "mls_purchase", "copy"))
            }
          >
            Copy MLS purchase link (purchase only, no login)
          </MenuItem>
        ) : null}
        {user.is_active ? (
          <MenuItem
            sx={{ color: "error.main" }}
            onClick={() => closeMenu(() => onDeactivate(user))}
          >
            Deactivate
          </MenuItem>
        ) : (
          <MenuItem
            disabled={updating}
            onClick={() => closeMenu(() => onReactivate(user))}
          >
            Reactivate
          </MenuItem>
        )}
      </Menu>
    </Row>
  )
}

UserRowActions.propTypes = {
  user: PropTypes.object.isRequired,
  updating: PropTypes.bool,
  onIssueLink: PropTypes.func.isRequired,
  onSubscription: PropTypes.func.isRequired,
  onBoards: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
  onBookingLink: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
}

export default function AdminUsers() {
  const navigate = useNavigate()
  const profile = useSelector(selectAdminProfile)
  const podDialerOn = Boolean(profile?.features?.pod_dialer)
  const campaigns = useCustomerAccessCampaigns()
  const [customerAccessOpen, setCustomerAccessOpen] = useState(false)
  const [creatingAccess, setCreatingAccess] = useState(false)
  const [formLinkCopied, setFormLinkCopied] = useState(false)
  const [smsCreateOpen, setSmsCreateOpen] = useState(false)
  const [creatingSms, setCreatingSms] = useState(false)
  const [deactivation, setDeactivation] = useState(null)
  const [updatingId, setUpdatingId] = useState(null)
  const [editUser, setEditUser] = useState(null)
  const [bookingLinkUser, setBookingLinkUser] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteUser, setDeleteUser] = useState(null)
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [conciergeChange, setConciergeChange] = useState(null)
  const [boardsUser, setBoardsUser] = useState(null)
  const [assignOwner, setAssignOwner] = useState(null)
  const [notice, setNotice] = useState(null)

  const query = useAdminPaginatedQuery(adminApi.fetchUsers, {
    filters: USER_FILTER_SPECS,
    defaultSortColumn: "name",
    defaultPageSize: 50,
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
  } = query

  const toolbarFilters = [
    {
      key: "active",
      label: "Active only",
      type: "switch",
      onValue: "true",
      offValue: "all",
      chip: true,
      chipLabel: "Including inactive",
    },
    {
      key: "type",
      label: "Type",
      type: "multi",
      options: podDialerOn
        ? [...USER_TYPE_OPTIONS, POD_AGENTS_TYPE_OPTION]
        : USER_TYPE_OPTIONS,
    },
    {
      key: "sub",
      label: "Subscription",
      type: "multi",
      options: SUBSCRIPTION_OPTIONS,
    },
    {
      key: "mls",
      label: "Boards",
      type: "entity",
      entity: "mls",
      staticOptions: MLS_STATIC_OPTIONS,
      minWidth: 180,
    },
    {
      key: "concierge",
      label: "Concierge",
      type: "entity",
      entity: "concierge",
      staticOptions: CONCIERGE_STATIC_OPTIONS,
      minWidth: 180,
    },
    {
      key: "login",
      label: "Last login",
      type: "select",
      options: LAST_LOGIN_OPTIONS,
    },
    { key: "joined", label: "Joined", type: "select", options: JOINED_OPTIONS },
  ]

  async function issueLink(user, kind, mode) {
    // Open the tab synchronously so popup blockers allow it, then point it at
    // the freshly signed link once the API returns it.
    const tab = mode === "open" ? window.open("", "_blank") : null
    try {
      const { data } = await adminApi.issueUserLoginLink(user.id, kind)
      if (mode === "open") {
        if (tab) {
          tab.opener = null
          tab.location.href = data.url
        } else {
          window.open(data.url, "_blank", "noopener,noreferrer")
        }
      } else {
        await navigator.clipboard?.writeText(data.url)
        setNotice("Link copied")
      }
    } catch (error) {
      tab?.close()
      setNotice(error?.response?.data?.detail || "Could not create that link.")
    }
  }

  async function confirmConciergeChange() {
    if (!conciergeChange) return
    const { user, checked } = conciergeChange
    setUpdatingId(user.id)
    try {
      await adminApi.updateUser(user.id, { is_concierge: checked })
      setConciergeChange(null)
      refresh()
    } finally {
      setUpdatingId(null)
    }
  }

  async function handleSaveUser(form) {
    if (!editUser) return
    setSaving(true)
    try {
      const payload = { ...form }
      // Pod membership can't be edited while the Pod Dialer is off.
      if (!podDialerOn) delete payload.pod_ids
      if (!profile?.is_superuser) {
        delete payload.is_staff
        delete payload.is_superuser
      }
      const { data } = await adminApi.updateUser(editUser.id, payload)
      setEditUser(null)
      if (data?.deactivation) {
        setDeactivation({ email: editUser.email, ...data.deactivation })
      }
      refresh()
    } finally {
      setSaving(false)
    }
  }

  async function handleCreateConcierge(form) {
    setCreating(true)
    try {
      await adminApi.createConciergeUser({
        email: form.email.toLowerCase().trim(),
        password: form.password,
        password_confirm: form.password_confirm,
        name: form.name.trim(),
        is_onebase_staff: form.is_onebase_staff,
        is_active: form.is_active,
      })
      setCreateOpen(false)
      refresh()
    } catch (err) {
      throw err
    } finally {
      setCreating(false)
    }
  }

  async function handleCreateSmsEmployee(payload) {
    setCreatingSms(true)
    try {
      await adminApi.createSmsEmployee(payload)
      setSmsCreateOpen(false)
      refresh()
    } finally {
      setCreatingSms(false)
    }
  }

  async function handleReactivate(user) {
    setUpdatingId(user.id)
    try {
      await adminApi.updateUser(user.id, { is_active: true })
      refresh()
    } finally {
      setUpdatingId(null)
    }
  }

  async function handleCreateCustomerAccess(form) {
    setCreatingAccess(true)
    try {
      const res = await adminApi.createCustomerAccess(
        toCustomerAccessPayload(form)
      )
      refresh()
      return res.data
    } finally {
      setCreatingAccess(false)
    }
  }

  function copySupportFormLink() {
    navigator.clipboard
      ?.writeText(`${window.location.origin}${CUSTOMER_ACCESS_FORM_PATH}`)
      .then(() => {
        setFormLinkCopied(true)
        window.setTimeout(() => setFormLinkCopied(false), 1500)
      })
  }

  async function handleDeleteUser() {
    if (!deleteUser) return
    setDeleting(true)
    try {
      const res = await adminApi.deleteUser(deleteUser.id)
      if (res?.data && typeof res.data === "object") {
        setDeactivation({ email: deleteUser.email, ...res.data })
      }
      setDeleteUser(null)
      refresh()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Users"
        subtitle="Find a customer and see their subscription, boards and concierge in one place."
        actions={
          <Row gap={1} sx={{ flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={copySupportFormLink}>
              {formLinkCopied ? "Copied" : "Copy support form link"}
            </Button>
            <Button
              variant="contained"
              onClick={() => setCustomerAccessOpen(true)}
            >
              New customer access
            </Button>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              New concierge
            </Button>
            <Button variant="contained" onClick={() => setSmsCreateOpen(true)}>
              New texting employee
            </Button>
          </Row>
        }
      />
      <AdminListToolbar
        query={query}
        searchLabel="Search name or email"
        noun={totalResultsSize === 1 ? "user" : "users"}
        filters={toolbarFilters}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={10}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="name"
              label="Name / Email"
              query={query}
              minWidth={{ xs: 140, sm: 200 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 88, sm: 110 }}>
              Type
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="subscription"
              label="Subscription"
              query={query}
              minWidth={{ xs: 120, sm: 150 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 130 }}>
              Boards
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 130 }}>
              Concierge
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="last_login"
              label="Last login"
              query={query}
              minWidth={{ xs: 88, sm: 110 }}
            />
            <AdminSortableHeaderCell
              columnName="date_joined"
              label="Joined"
              query={query}
              minWidth={{ xs: 88, sm: 110 }}
            />
            <AdminSortableHeaderCell
              columnName="is_concierge"
              label="Concierge flag"
              query={query}
              minWidth={{ xs: 64, sm: 88 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 48, sm: 64 }}>
              Active
            </AdminHeaderTableCell>
            <AdminHeaderTableCell align="right" minWidth={{ xs: 160, sm: 220 }}>
              Actions
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((user) => (
          <AdminTableRow key={user.id} hover>
            <NameEmailCell user={user} />
            <TypeCell user={user} />
            <SubscriptionCell user={user} />
            <BoardsCell user={user} />
            <ConciergeNamesCell user={user} />
            <DateCell value={user.last_login} relative />
            <DateCell value={user.date_joined} />
            <TableCell sx={adminTableBodyCellSx}>
              <Checkbox
                checked={Boolean(user.is_concierge)}
                disabled={updatingId === user.id}
                onChange={(e) =>
                  setConciergeChange({ user, checked: e.target.checked })
                }
                size="small"
                inputProps={{
                  "aria-label": `Concierge flag for ${user.email}`,
                }}
              />
            </TableCell>
            <TableCell sx={adminTableBodyCellSx}>
              {user.is_active ? "Yes" : "No"}
            </TableCell>
            <TableCell align="right" sx={adminTableBodyCellSx}>
              <UserRowActions
                user={user}
                updating={updatingId === user.id}
                onIssueLink={issueLink}
                onSubscription={(row) =>
                  navigate(`/admin/trial-management?user=${row.id}`)
                }
                onBoards={setBoardsUser}
                onAssign={setAssignOwner}
                onBookingLink={setBookingLinkUser}
                onEdit={setEditUser}
                onDeactivate={setDeleteUser}
                onReactivate={handleReactivate}
              />
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>

      <Dialog
        open={Boolean(conciergeChange)}
        onClose={() => setConciergeChange(null)}
      >
        <DialogTitle>
          {conciergeChange?.checked
            ? "Make this user a concierge?"
            : "Remove concierge access?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {conciergeChange?.checked
              ? "They will be able to log in as a concierge and be assigned to parent accounts."
              : "They will no longer be treated as a concierge. Existing assignments are not revoked."}{" "}
            User: <strong>{conciergeChange?.user?.email}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConciergeChange(null)}
            disabled={Boolean(updatingId)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={conciergeChange?.checked ? "primary" : "error"}
            onClick={confirmConciergeChange}
            disabled={Boolean(updatingId)}
          >
            {conciergeChange?.checked ? "Make concierge" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <AdminUserMlsBoardsDialog
        open={Boolean(boardsUser)}
        user={boardsUser}
        onClose={(changed) => {
          setBoardsUser(null)
          if (changed) refresh()
        }}
      />

      <AdminAssignConciergeDialog
        open={Boolean(assignOwner)}
        owner={assignOwner}
        onClose={() => setAssignOwner(null)}
        onAssigned={() => {
          setAssignOwner(null)
          setNotice("Concierge assigned")
          refresh()
        }}
      />

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={2500}
        onClose={() => setNotice(null)}
        message={notice}
      />

      <AdminConciergeUserCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreateConcierge}
        saving={creating}
      />

      <AdminSmsEmployeeCreateDialog
        open={smsCreateOpen}
        onClose={() => setSmsCreateOpen(false)}
        onCreate={handleCreateSmsEmployee}
        saving={creatingSms}
        podDialerOn={podDialerOn}
      />

      <AdminCustomerAccessCreateDialog
        open={customerAccessOpen}
        onClose={() => setCustomerAccessOpen(false)}
        onCreate={handleCreateCustomerAccess}
        saving={creatingAccess}
        campaigns={campaigns}
      />

      <BookingLinkDialog
        open={Boolean(bookingLinkUser)}
        user={bookingLinkUser}
        onClose={() => setBookingLinkUser(null)}
      />
      <AdminUserEditDialog
        open={Boolean(editUser)}
        user={editUser}
        canEditStaffFlags={Boolean(profile?.is_superuser)}
        onClose={() => setEditUser(null)}
        onSave={handleSaveUser}
        saving={saving}
        podDialerOn={podDialerOn}
      />

      <Dialog open={Boolean(deleteUser)} onClose={() => setDeleteUser(null)}>
        <DialogTitle>Deactivate user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will set <strong>{deleteUser?.email}</strong> to inactive,
            matching Django admin delete behavior for linked accounts.
            {deleteUser?.is_sms_only || deleteUser?.pods?.length
              ? " They also leave their pods, drop out of any live dialing and are logged out; their numbers and history stay."
              : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" onClick={handleDeleteUser} disabled={deleting}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(deactivation)}
        onClose={() => setDeactivation(null)}
      >
        <DialogTitle>Deactivated {deactivation?.email}</DialogTitle>
        <DialogContent>
          <Column gap={1}>
            {deactivation?.pods_left ? (
              <DialogContentText>
                Removed from {deactivation.pods_left} pod
                {deactivation.pods_left === 1 ? "" : "s"}.
              </DialogContentText>
            ) : null}
            {deactivation?.numbers_kept?.length ? (
              <Alert severity="warning">
                Still assigned to them:{" "}
                {deactivation.numbers_kept.map((n) => n.phone).join(", ")}.
                Reassign or release these on Twilio calling numbers.
              </Alert>
            ) : (
              <DialogContentText>
                No numbers were assigned to them.
              </DialogContentText>
            )}
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivation(null)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Column>
  )
}
